'use client';

import { useSession } from 'next-auth/react';
import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useFormik } from 'formik';
import styles from './ForgotPasswordPage.module.scss';
import Input from '../../../UIKit/Input/Input';
import Button from '../../../UIKit/Button/Button';
import Svg from '../../../UIKit/Svg';
import apiClient, { ApiError } from '@/src/utils/client';
import { NotificationStatus, notify } from '@/src/utils/notifications';
import { useSchema } from './useSchema';

const ForgotPasswordPage = () => {
  const t = useTranslations('forgotPasswordPage');
  const validationSchema = useSchema();
  const router = useRouter();
  const localeCode = useLocale();
  const session = useSession();

  const onForgotPassword = async ({ email }: { email: string }) => {
    let data;
    try {
      data = await apiClient.withoutAuth().post(
        session,
        `auth/forgot-password?locale=${localeCode}`,
        200,
        { body: JSON.stringify({ email }) },
      );
    } catch (e) {
      const error = e as ApiError;
      notify({
        text: { title: t('Failed to send password reset link'), body: error.message },
        status: NotificationStatus.ERROR,
      });
      console.error(`${t('Failed to send password reset link')}:`, error);
    }

    if (data?.statusCode === 200) {
      notify({
        text: { title: t('Password reset link sent') },
        status: NotificationStatus.SUCCESS,
      });
      router.push('/check-email');
    }
  };

  const {
    values, errors, handleChange, handleSubmit, handleBlur, isValid,
  } = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: ({ email }) => onForgotPassword({ email }),
  });

  return (
    <>
      <div className={styles.info}>
        <h1 className={styles.title}>{t('Forgot your password?')}</h1>
        <p className={styles.subtitle}>{t('Enter the email address')}</p>
      </div>
      <form
        className={styles.form}
        onSubmit={handleSubmit}
      >
        <div className={styles.form__fields}>
          <Input
            id='forgotPasswordEmailInput'
            label={t('Email')}
            name='email'
            type='email'
            value={values.email}
            setValue={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.email}
          />
        </div>
        <Button
          className={styles.form__button}
          type='submit'
          disabled={!isValid}
        >
          {t('Send')}
        </Button>
      </form>
      <Link
        className={styles.backLink}
        href='/login'
      >
        <div className={styles.arrowBackIcon}>
          <Svg name='arrowBack' />
        </div>
        {t('Back to log in')}
      </Link>
    </>
  );
};

export default ForgotPasswordPage;
